"use client"

import { LoginForm } from "@/components/LoginForm";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoginFormSchema } from "@/schemas/login-form.schema";
import { cbLogin } from "@/services/auth.service";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { z } from "zod";

import { validateToken } from "@/services/auth.service";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { Info } from "lucide-react";
import { getVersion, VersionInfo } from "@/services/version.service";
import { useEffect } from "react";
import { AxiosError } from "axios";

export default function Login() {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");
    const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
    const navigate = useNavigate();
    const { setCurrentUser } = useCurrentUser();

    useEffect(() => {
        const fetchVersion = async () => {
            try {
                const version = await getVersion();
                setVersionInfo(version);
            } catch (error) {
                console.error('Failed to fetch version:', error);
            }
        };
        fetchVersion();
    }, []);

    const handleOnSubmit = async (data: z.infer<typeof LoginFormSchema>) => {
        if (isLoading) return; // Prevent multiple submissions
        
        setIsLoading(() => true);
        setError("");
        
        try {
            const token = await cbLogin(data.email, data.password);
            const user = await validateToken(token);
            setCurrentUser(token, user);
            navigate("/dashboard");
        } catch (error) {
            if (error instanceof AxiosError) {
                if(error.status == 429){
                    setError("Too many requests");
                } else {
                    setError("Invalid credentials");
                }
            } else {
                setError("Invalid credentials");
            }
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <div className="flex items-center justify-center h-screen relative dark:bg-black dark:text-white">
            <Card className="w-[400px] dark:bg-[#1C1C1C] dark:text-white">
                <CardHeader>
                    <CardTitle>Login</CardTitle>
                </CardHeader>
                <CardContent>
                    {error && (
                        <Card className="mb-4 border-red-500 bg-red-50 dark:bg-red-950">
                            <CardHeader className="flex flex-row items-center gap-2 py-1 px-2">
                                <Info className="w-5 h-5 text-red-500" />
                                <CardTitle className="text-red-700 text-sm font-semibold">{error}</CardTitle>
                            </CardHeader>
                        </Card>
                    )}
                    <LoginForm isLoading={isLoading} onSubmit={handleOnSubmit} />
                </CardContent>
            </Card>
            
            {/* Version info in lower left corner */}
            {versionInfo && (
                <div className="absolute bottom-4 left-4 text-xs text-gray-500 dark:text-gray-400">
                    <div>v{versionInfo["CB-Admin-v2"]}</div>
                    <div>Build: {versionInfo["Build"]}</div>
                </div>
            )}
        </div>
    )
}